import { Button, cx, Icon, Notice, VoiceWave } from '@raro/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useWakeLock } from '../lib/wake-lock';

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? 'http://localhost:4000';

type Status = 'idle' | 'connecting' | 'live' | 'ended' | 'error';

/**
 * Voz contínua: a pessoa conversa com a Machine como numa ligação (OpenAI Realtime, WebRTC).
 * A chave real fica no servidor: o navegador recebe uma chave efêmera, abre a chamada direto com a OpenAI,
 * e cada fala transcrita (dela e da Machine) é gravada na conversa pelo nosso endpoint de log.
 */
export function RealtimeVoice({ token, onTurn, onEnd, onFinish, disabled, autoStart, onActive, caption, onTrouble }: { token: string; onTurn: (role: 'user' | 'assistant', text: string) => void; onEnd: () => void; /** a Machine se despediu e pediu para encerrar: a página fecha a conversa e mostra a tela de fim */ onFinish: () => void; disabled?: boolean; /** entra na linha ao montar (a pessoa acabou de clicar em começar falando) */ autoStart?: boolean; /** a página esconde a transcrição enquanto a voz está ativa */ onActive?: (active: boolean) => void; /** última fala da Machine, para a legenda do palco */ caption?: string | null; /** a voz falhou (erro, conexão, silêncio): a página segue por texto */ onTrouble?: (detail: string) => void }) {
  const [status, setStatus] = useState<Status>('idle');
  // conversa por voz no celular: a tela não pode apagar no meio (o navegador corta o microfone)
  useWakeLock(status === 'connecting' || status === 'live');
  const [error, setError] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [level, setLevelState] = useState<'quiet' | 'user' | 'machine'>('quiet');
  const setLevel = (l: 'quiet' | 'user' | 'machine') => { levelRef.current = l; setLevelState(l); };
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const endingRef = useRef(false);
  const promptRef = useRef('');
  const baseRef = useRef('');
  /** chamada de ferramenta vista nesta resposta; é tratada só no response.done, quando a OpenAI aceita uma resposta nova */
  const pendingToolRef = useRef<{ callId: string; confirmacao: string } | null>(null);
  const rejectionsRef = useRef(0);
  const watchdogRef = useRef<number | null>(null);
  const troubledRef = useRef(false);
  /** cão de guarda: depois que a pessoa fala (ou da abertura), a Machine tem que responder em até 20 s */
  const expectResponse = (ms = 20000) => { if (watchdogRef.current) window.clearTimeout(watchdogRef.current); watchdogRef.current = window.setTimeout(() => trouble('a Machine não respondeu a tempo'), ms); };
  const gotResponse = () => { if (watchdogRef.current) window.clearTimeout(watchdogRef.current); watchdogRef.current = null; };
  /** eco do transcritor: com ruído ele devolve a dica de vocabulário como se fosse fala; isso não é um turno */
  const isEcho = (t: string) => { const n = t.trim().toLowerCase(); return n.length < 2 || (promptRef.current.length > 10 && n.startsWith(promptRef.current)); };
  const audioCtxRef = useRef<AudioContext | null>(null);
  const micAnalyserRef = useRef<AnalyserNode | null>(null);
  const outAnalyserRef = useRef<AnalyserNode | null>(null);
  const levelRef = useRef<'quiet' | 'user' | 'machine'>('quiet');
  const binsRef = useRef<Uint8Array | null>(null);
  /** espectro do que está soando agora: microfone quando a pessoa fala, saída quando é a Machine */
  const getLevels = useCallback(() => {
    const an = levelRef.current === 'user' ? micAnalyserRef.current : levelRef.current === 'machine' ? outAnalyserRef.current : null;
    if (!an) return null;
    if (!binsRef.current || binsRef.current.length !== an.frequencyBinCount) binsRef.current = new Uint8Array(new ArrayBuffer(an.frequencyBinCount));
    an.getByteFrequencyData(binsRef.current as Uint8Array<ArrayBuffer>);
    return binsRef.current;
  }, []);
  const analyse = (stream: MediaStream, slot: 'mic' | 'out') => {
    try {
      const ctx = audioCtxRef.current ?? new AudioContext();
      audioCtxRef.current = ctx;
      void ctx.resume();
      const src = ctx.createMediaStreamSource(stream);
      const an = ctx.createAnalyser();
      an.fftSize = 256; an.smoothingTimeConstant = 0.6;
      src.connect(an); // só analisa; a reprodução continua pelo <audio>
      if (slot === 'mic') micAnalyserRef.current = an; else outAnalyserRef.current = an;
    } catch { /* sem Web Audio: o palco respira sem reagir */ }
  };
  const finishTimer = useRef<number | null>(null);

  const log = async (role: 'user' | 'assistant', text: string) => {
    if (!text.trim()) return;
    onTurn(role, text.trim());
    try { await fetch(`${API_URL}/conversa/${token}/log`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ role, text: text.trim() }) }); } catch { /* fica só na tela */ }
  };

  const stop = (ended = true) => {
    void audioCtxRef.current?.close().catch(() => undefined); audioCtxRef.current = null; micAnalyserRef.current = null; outAnalyserRef.current = null;
    onActive?.(false);
    dcRef.current?.close();
    pcRef.current?.getSenders().forEach((s) => s.track?.stop());
    pcRef.current?.close();
    micRef.current?.getTracks().forEach((t) => t.stop());
    pcRef.current = null; dcRef.current = null; micRef.current = null;
    if (ended) { setStatus('ended'); onEnd(); }
  };
  /** qualquer falha vira plano B: desliga a voz e entrega a página ao texto. uma vez só. */
  function trouble(detail: string) {
    if (troubledRef.current || endingRef.current) return;
    troubledRef.current = true;
    if (watchdogRef.current) window.clearTimeout(watchdogRef.current);
    stop(false);
    setStatus('error');
    setError(null);
    void fetch(`${API_URL}/conversa/${token}/trouble`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ detail }) }).catch(() => undefined);
    onTrouble?.(detail);
  }
  useEffect(() => () => { stop(false); if (finishTimer.current) window.clearTimeout(finishTimer.current); if (watchdogRef.current) window.clearTimeout(watchdogRef.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  /** A Machine chamou encerrar_conversa: espera o áudio da despedida acabar (ou 6 s), desliga tudo e avisa a página. */
  const hangUp = () => {
    if (finishTimer.current) window.clearTimeout(finishTimer.current);
    finishTimer.current = null;
    stop(false);
    setStatus('ended');
    onFinish();
  };
  const scheduleHangUp = (ms: number) => { if (finishTimer.current) window.clearTimeout(finishTimer.current); finishTimer.current = window.setTimeout(hangUp, ms); };

  const start = async () => {
    setError(null);
    setStatus('connecting');
    onActive?.(true);
    try {
      const sess = await fetch(`${API_URL}/conversa/${token}/realtime`, { method: 'POST' });
      if (!sess.ok) { const msg = (await sess.json().catch(() => ({}))).message ?? 'não consegui abrir a voz contínua'; trouble(String(msg)); return; }
      const { clientSecret, callUrl, opening, instructions, transcriptionPrompt } = (await sess.json()) as { clientSecret: string; callUrl: string; model: string; opening: string; instructions: string; transcriptionPrompt?: string };
      promptRef.current = (transcriptionPrompt ?? '').slice(0, 40).toLowerCase();
      baseRef.current = instructions;
      const mic = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
      micRef.current = mic;
      analyse(mic, 'mic');
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      pc.onconnectionstatechange = () => { if (pc.connectionState === 'failed' || pc.connectionState === 'disconnected') trouble(`conexão ${pc.connectionState}`); };
      const audio = audioRef.current ?? new Audio();
      audio.autoplay = true;
      audioRef.current = audio;
      pc.ontrack = (e) => { const st = e.streams[0] ?? null; audio.srcObject = st; if (st) analyse(st, 'out'); };
      for (const track of mic.getTracks()) pc.addTrack(track, mic);
      const dc = pc.createDataChannel('oai-events');
      dcRef.current = dc;
      dc.onopen = () => {
        setStatus('live');
        // a Machine abre a conversa (opening = prompt inteiro + ordem de abrir, porque response.instructions substitui as da sessão)
        dc.send(JSON.stringify({ type: 'response.create', response: { instructions: opening } }));
        expectResponse(25000);
      };
      dc.onmessage = (m) => {
        try {
          const ev = JSON.parse(m.data as string) as { type: string; transcript?: string; delta?: string; name?: string; call_id?: string; arguments?: string; response?: { id?: string; usage?: Record<string, unknown> } };
          if (ev.type === 'response.function_call_arguments.done' && ev.name === 'encerrar_conversa') {
            let confirmacao = '';
            try { confirmacao = String((JSON.parse(ev.arguments ?? '{}') as { confirmacao?: string }).confirmacao ?? '').trim(); } catch { /* sem argumentos */ }
            pendingToolRef.current = { callId: ev.call_id ?? '', confirmacao };
          }
          else if (endingRef.current && ev.type === 'output_audio_buffer.stopped') { scheduleHangUp(900); }
          if (ev.type === 'conversation.item.input_audio_transcription.completed' && ev.transcript) { setLevel('quiet'); if (!isEcho(ev.transcript)) void log('user', ev.transcript); }
          else if ((ev.type === 'response.output_audio_transcript.done' || ev.type === 'response.audio_transcript.done') && ev.transcript) { void log('assistant', ev.transcript); }
          else if (ev.type === 'input_audio_buffer.speech_started') setLevel('user');
          else if (ev.type === 'response.output_audio.delta' || ev.type === 'response.audio.delta') setLevel('machine');
          else if (ev.type === 'response.done') {
            setLevel('quiet');
            const tool = pendingToolRef.current;
            pendingToolRef.current = null;
            if (tool && !endingRef.current) {
              // encerrar é um acordo: a chamada só vale com as palavras da pessoa autorizando. tratado aqui, depois do response.done, para a resposta nova ser aceita.
              // tool_choice none: a próxima resposta tem que ser FALADA; sem isso a Machine chamava a ferramenta de novo e ficava num laço mudo.
              if (tool.confirmacao.length >= 2 || rejectionsRef.current >= 2) {
                endingRef.current = true;
                dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: tool.callId, output: JSON.stringify({ ok: true, instrucao: 'a ligação será encerrada depois da sua despedida.' }) } }));
                dc.send(JSON.stringify({ type: 'response.create', response: { tool_choice: 'none', instructions: `${baseRef.current}\n\nAGORA: a pessoa autorizou encerrar. Despeça-se em duas ou três frases faladas, calorosas: agradeça pelo nome, diga o que acontece a seguir (a Raro vai ler tudo com calma e volta a falar com a pessoa) e deseje um bom dia. Não faça perguntas. A ligação encerra sozinha depois.` } }));
                scheduleHangUp(20000);
              } else {
                rejectionsRef.current += 1;
                dc.send(JSON.stringify({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: tool.callId, output: JSON.stringify({ ok: false, motivo: 'a pessoa ainda não autorizou encerrar.' }) } }));
                dc.send(JSON.stringify({ type: 'response.create', response: { tool_choice: 'none', instructions: `${baseRef.current}\n\nAGORA: fale com a pessoa (não chame ferramenta): faça o resumo curto do que ouviu, pergunte se ela quer acrescentar algo e pergunte "posso encerrar por aqui?". Espere a resposta dela.` } }));
              }
            } else if (endingRef.current) scheduleHangUp(12000); if (ev.response?.usage) void fetch(`${API_URL}/conversa/${token}/usage`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ responseId: ev.response.id, usage: ev.response.usage }) }).catch(() => undefined); }
          else if (ev.type === 'error') { const msg = String((ev as unknown as { error?: { message?: string } }).error?.message ?? ev.type); if (/credit|billing|quota|rate limit|insufficient|unavailable|overloaded|server error/i.test(msg)) trouble(msg); else setError(msg); }
          else if (ev.type === 'input_audio_buffer.speech_stopped') expectResponse(20000);
          else if (ev.type === 'response.created') gotResponse();
        } catch { /* evento que não interessa */ }
      };
      dc.onclose = () => { if (status !== 'ended') setStatus('ended'); };
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      const r = await fetch(callUrl, { method: 'POST', headers: { authorization: `Bearer ${clientSecret}`, 'content-type': 'application/sdp' }, body: offer.sdp });
      if (!r.ok) { trouble(`a OpenAI recusou a chamada (${r.status})`); return; }
      await pc.setRemoteDescription({ type: 'answer', sdp: await r.text() });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (/NotAllowed|Permission|denied/i.test(msg)) { setError('preciso do microfone para a voz contínua. libere o acesso ou continue escrevendo.'); setStatus('error'); stop(false); }
      else trouble(msg);
    }
  };

  useEffect(() => { if (autoStart && status === 'idle') void start(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMute = () => { const next = !muted; setMuted(next); micRef.current?.getAudioTracks().forEach((t) => { t.enabled = !next; }); };

  const active = status === 'connecting' || status === 'live';
  const waveState = status === 'connecting' ? 'connecting' : status === 'live' ? level : 'idle';
  return (
    <div className={cx('rr-live', status === 'live' && 'rr-live--on', level === 'user' && 'rr-live--user', level === 'machine' && 'rr-live--machine', active && 'rr-live--stage')}>
      {!active ? (
        <div className="rr-row rr-row--between rr-row--wrap">
          <span className="rr-faint" style={{ fontSize: 'var(--rr-text-sm)' }}>{status === 'ended' ? 'a ligação terminou. dá para ligar de novo, ou trocar para mensagem e continuar por áudio gravado ou texto.' : 'a ligação ainda não começou. ao ligar, a Machine fala e escuta ao vivo: você só conversa, e pode interromper.'}</span>
          <Button variant="primary" onClick={start} disabled={disabled}><Icon name="phone" size={14} /> {status === 'ended' ? 'ligar de novo' : 'ligar para a Machine'}</Button>
        </div>
      ) : (
        <div className="rr-voice">
          <VoiceWave state={waveState} getLevels={getLevels} />
          <span className="rr-voice__mode"><span className="rr-dot rr-dot--active" /> {status === 'connecting' ? 'ligando' : 'em ligação · ao vivo'}</span>
          <span className={cx('rr-voice__status', level === 'user' && 'rr-voice__status--user', level === 'machine' && 'rr-voice__status--machine')}>{status === 'connecting' ? 'conectando' : muted ? 'seu microfone está desligado' : level === 'user' ? 'ouvindo você' : level === 'machine' ? 'a Machine está falando' : 'pode falar'}</span>
          <p className="rr-voice__caption">{status === 'live' && caption ? caption : ''}</p>
          <div className="rr-voice__actions">
            <button type="button" className={cx('rr-voice__mute', muted && 'rr-voice__mute--on')} onClick={toggleMute} title={muted ? 'ligar microfone' : 'silenciar'} aria-label={muted ? 'ligar microfone' : 'silenciar'}><Icon name={muted ? 'mic-off' : 'mic'} size={20} /></button>
            <button type="button" className="rr-voice__end" onClick={() => { stop(false); setStatus('ended'); onFinish(); }} title="encerrar a conversa inteira" aria-label="encerrar a conversa inteira"><Icon name="x" size={20} strokeWidth={2} /></button>
          </div>
          <div className="rr-voice__actions">
            <button type="button" className="rr-conversa__transcript-toggle" onClick={() => stop(true)}>desligar e continuar por mensagem</button>
          </div>
        </div>
      )}
      {error && <Notice tone="error">{error}</Notice>}
    </div>
  );
}
